import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useEditGuard } from './useEditGuard';

export interface Tag {
  id: number;
  name: string;
  space_id: string | null;
  user_id: string;
  created_at: string;
}

interface UseTagsOptions {
  spaceId?: string | null; // null = personal space
}

export function useTags({ spaceId }: UseTagsOptions = {}) {
  const allowEdit = useEditGuard();
  const { user } = useAuth();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTags = useCallback(async () => {
    if (!user) { setTags([]); setLoading(false); return; }

    try {
      let query = supabase
        .from('tags')
        .select('*')
        .order('name', { ascending: true });

      if (spaceId) {
        query = query.eq('space_id', spaceId);
      } else {
        query = query.is('space_id', null).eq('user_id', user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTags(data || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  }, [user, spaceId]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  // Create a new tag (normalised to lowercase, deduped)
  const createTag = useCallback(async (name: string): Promise<Tag | null> => {
    if (!user || !name.trim()) return null;
    if (!allowEdit()) return null;
    const normalised = name.trim().toLowerCase();

    // Return existing if already present
    const existing = tags.find(t => t.name === normalised);
    if (existing) return existing;

    const { data, error } = await supabase
      .from('tags')
      .insert([{ name: normalised, space_id: spaceId ?? null, user_id: user.id }])
      .select()
      .single();

    if (error) {
      // Unique constraint violation — fetch the existing row
      if (error.code === '23505') {
        const { data: existing } = await supabase
          .from('tags')
          .select('*')
          .eq('name', normalised)
          .eq('user_id', user.id)
          .is('space_id', spaceId ?? null)
          .single();
        return existing ?? null;
      }
      console.error('Error creating tag:', error);
      return null;
    }

    if (data) setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  }, [user, spaceId, tags]);

  const deleteTag = useCallback(async (id: number) => {
    if (!allowEdit()) return;
    const { error } = await supabase.from('tags').delete().eq('id', id);
    if (!error) setTags(prev => prev.filter(t => t.id !== id));
  }, []);

  const renameTag = useCallback(async (id: number, name: string) => {
    if (!allowEdit()) return;
    const normalised = name.trim().toLowerCase();
    if (!normalised) return;
    const { error } = await supabase.from('tags').update({ name: normalised }).eq('id', id);
    if (!error) setTags(prev =>
      prev.map(t => t.id === id ? { ...t, name: normalised } : t)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, []);

  // Fuzzy search: returns tags whose name contains the query string
  const searchTags = useCallback((query: string): Tag[] => {
    if (!query.trim()) return tags;
    const q = query.trim().toLowerCase();
    return tags.filter(t => t.name.includes(q));
  }, [tags]);

  // Assign a set of tags to an event (replaces existing links)
  const setEventTags = useCallback(async (eventId: number | string, tagIds: number[]) => {
    if (!allowEdit()) return;
    // Delete existing links for this event
    await supabase.from('event_tag_links').delete().eq('event_id', String(eventId));
    if (tagIds.length === 0) return;
    await supabase.from('event_tag_links').insert(
      tagIds.map(tag_id => ({ event_id: String(eventId), tag_id }))
    );
  }, []);

  // Assign a set of tags to a todo (replaces existing links)
  const setTodoTags = useCallback(async (todoId: number | string, tagIds: number[]) => {
    if (!allowEdit()) return;
    await supabase.from('todo_tag_links').delete().eq('todo_id', String(todoId));
    if (tagIds.length === 0) return;
    await supabase.from('todo_tag_links').insert(
      tagIds.map(tag_id => ({ todo_id: String(todoId), tag_id }))
    );
  }, []);

  // Fetch event IDs that have a specific tag (for tag-filter in calendar view)
  const fetchEventIdsByTag = useCallback(async (tagId: number): Promise<Set<string>> => {
    const { data } = await supabase
      .from('event_tag_links')
      .select('event_id')
      .eq('tag_id', tagId);
    return new Set((data || []).map((r: any) => String(r.event_id)));
  }, []);

  // Fetch todo IDs that have a specific tag (for tag-filter in board view)
  const fetchTodoIdsByTag = useCallback(async (tagId: number): Promise<Set<string>> => {
    const { data } = await supabase
      .from('todo_tag_links')
      .select('todo_id')
      .eq('tag_id', tagId);
    return new Set((data || []).map((r: any) => String(r.todo_id)));
  }, []);

  // Fetch tags for a list of event IDs — returns map of eventId → Tag[]
  const fetchEventTags = useCallback(async (eventIds: (number | string)[]): Promise<Map<string, Tag[]>> => {
    if (eventIds.length === 0) return new Map();
    const { data } = await supabase
      .from('event_tag_links')
      .select('event_id, tags(*)')
      .in('event_id', eventIds.map(String));

    const map = new Map<string, Tag[]>();
    (data || []).forEach((row: any) => {
      const key = String(row.event_id);
      if (!map.has(key)) map.set(key, []);
      if (row.tags) map.get(key)!.push(row.tags as Tag);
    });
    return map;
  }, []);

  // Fetch tags for a list of todo IDs — returns map of todoId → Tag[]
  const fetchTodoTags = useCallback(async (todoIds: (number | string)[]): Promise<Map<string, Tag[]>> => {
    if (todoIds.length === 0) return new Map();
    const { data } = await supabase
      .from('todo_tag_links')
      .select('todo_id, tags(*)')
      .in('todo_id', todoIds.map(String));

    const map = new Map<string, Tag[]>();
    (data || []).forEach((row: any) => {
      const key = String(row.todo_id);
      if (!map.has(key)) map.set(key, []);
      if (row.tags) map.get(key)!.push(row.tags as Tag);
    });
    return map;
  }, []);

  return {
    tags,
    loading,
    createTag,
    deleteTag,
    renameTag,
    searchTags,
    setEventTags,
    setTodoTags,
    fetchEventTags,
    fetchTodoTags,
    fetchEventIdsByTag,
    fetchTodoIdsByTag,
    refetch: fetchTags,
  };
}
